import {motion} from "framer-motion"
import {FC, useEffect, useState} from "react"
import {MemoryRouter} from "react-router-dom"
import shadowRoot from "react-shadow"

import stylesheet from "@frontend/routes/globals.css?inline"
import {APP_ROOT_ID, SHADOW_HOST_ID} from "@frontend/utils/getAppRoot"

import {ChatPanelBody} from "./ChatPanelBody"
import {ChatPanelHeader} from "./ChatPanelHeader"
import {
  WIDGET_BORDER_RADIUS,
  WIDGET_HEIGHT_BREAKPOINTS,
  WIDGET_MARGIN,
  WIDGET_MOBILE_BREAKPOINT,
  WIDGET_PANEL_HEIGHT,
  WIDGET_PANEL_WIDTH,
  WIDGET_SLIDEOUT_WIDTH,
} from "./styles"

/**
 * Compact typography overrides for the widget shadow DOM.
 * The main app uses 18 px body text which is too large for a 380 px panel.
 * These rules bring message text down to 16 px / 22 px line-height while
 * keeping a comfortable 1.375 ratio (best-practice for body copy).
 */
/** Main slideout panel fill (Figma SVG paint0_linear): vertical white → #0182BA. */
const SLIDEOUT_PANEL_BG =
  "linear-gradient(180deg, #ffffff 0%, #ffffff 38%, #7eb8d4 72%, #0182ba 100%)" as const

/**
 * Animated blue→cyan effects for the slideout panel.
 * - ::before: gradient left-edge border (attention strip).
 * - ::after: low-opacity, slowly drifting radial-gradient wash over the
 *   entire panel body, giving the whole surface a subtle "alive" feel.
 * Both calm down on hover/focus-within so typing remains distraction-free.
 */
const SLIDEOUT_ATTENTION_CLASS = "ri2-slideout-attention"
const SLIDEOUT_ATTENTION_CSS = `
  @keyframes ri2SlideoutBorderShift {
    0%   { background-position: 50% 0%; }
    50%  { background-position: 50% 100%; }
    100% { background-position: 50% 0%; }
  }
  @keyframes ri2SlideoutBorderGlow {
    0%, 100% { opacity: 0.75; }
    50%      { opacity: 1; }
  }
  .${SLIDEOUT_ATTENTION_CLASS}::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 3px;
    pointer-events: none;
    /* SVG paint1_linear stops, mapped to a vertical strip */
    background: linear-gradient(
      180deg,
      #015da0 0%,
      #00e1ff 18.27%,
      #91ccf0 32.69%,
      #015da0 49.52%,
      #91ccf0 64.42%,
      #81ddf7 74.52%,
      #70eeff 85.25%,
      #00a9d7 100%
    );
    background-size: 100% 300%;
    animation:
      ri2SlideoutBorderShift 4s linear infinite,
      ri2SlideoutBorderGlow 2.4s ease-in-out infinite;
    box-shadow: 0 0 16px rgba(0, 225, 255, 0.55);
    transition: width 0.25s ease, box-shadow 0.25s ease, opacity 0.25s ease;
    z-index: 1;
  }
  .${SLIDEOUT_ATTENTION_CLASS}:focus-within::before,
  .${SLIDEOUT_ATTENTION_CLASS}:hover::before {
    width: 2px;
    box-shadow: none;
    animation-play-state: paused;
    opacity: 0.9;
  }
`

/**
 * Subtle drifting gradient wash applied INSIDE the shadow DOM to #app-root.
 * Lives alongside WIDGET_COMPACT_CSS so it's scoped to the widget panel and
 * not leaked to host pages.
 */
const SLIDEOUT_BODY_WASH_CSS = `
  @keyframes ri2SlideoutBodyDrift {
    0%   { background-position:   0%   0%, 100% 100%, 50% 50%, 0 0; }
    50%  { background-position: 100%  50%,   0%  50%, 50% 50%, 0 0; }
    100% { background-position:   0%   0%, 100% 100%, 50% 50%, 0 0; }
  }
  #app-root.ri2-slideout-body {
    background: white;
    transition: background-image 0.6s ease;
  }
  #app-root.ri2-slideout-body.ri2-slideout-body-idle {
    background-image:
      radial-gradient(55% 45% at 12% 15%, rgba(0, 169, 215, 0.07), transparent 72%),
      radial-gradient(55% 50% at 92% 88%, rgba(0, 225, 255, 0.05), transparent 74%),
      radial-gradient(80% 55% at 50% 50%, rgba(1, 93, 160, 0.02), transparent 78%),
      ${SLIDEOUT_PANEL_BG};
    background-repeat: no-repeat;
    background-size: 180% 180%, 180% 180%, 220% 220%, 100% 100%;
    animation: ri2SlideoutBodyDrift 18s ease-in-out infinite;
  }
`

const WIDGET_COMPACT_CSS = `
  /* ── Message content ── */
  .msg-container p,
  .msg-container li,
  .msg-container span {
    font-size: 16px !important;
    line-height: 20px !important;
  }
  .msg-container strong {
    font-size: 16px !important;
    line-height: 22px !important;
  }

  /* ── Lists inside messages ── */
  .msg-container ol,
  .msg-container ul {
    padding-left: 20px !important;
  }

  /* ── Input field ── */
  div:has(> div > textarea[data-test-id="chat-input"]) {
    padding: 8px 12px !important;
    border-radius: 18px !important;
  }
  textarea[data-test-id="chat-input"],
  textarea[data-test-id="chat-input"] + div {
    font-size: 16px !important;
    line-height: 20px !important;
    max-height: 72px !important;
  }
`

/**
 * Desktop slideout has more horizontal real estate than the 380 px overlay,
 * so bump typography up on the welcome/starter page only. The in-conversation
 * chat (messages + input) intentionally keeps the compact sizing from
 * WIDGET_COMPACT_CSS so long transcripts stay readable without excessive
 * scrolling.
 */
const SLIDEOUT_TYPOGRAPHY_CSS = `
  /* ── Welcome / greeting (pre-conversation) ── */
  [data-widget-greeting-title="true"] {
    font-size: 24px !important;
    line-height: 30px !important;
  }
  [data-widget-greeting-subtitle="true"] {
    font-size: 17px !important;
    line-height: 24px !important;
  }


  /* ── Starter buttons ── */
  [data-widget-starter="true"] {
    font-size: 17px !important;
    line-height: 1.4 !important;
    padding: 14px 18px !important;
    border-radius: 14px !important;
    gap: 14px !important;
  }
  [data-widget-starter="true"] svg {
    width: 22px !important;
    height: 22px !important;
  }

  @media (max-height: ${WIDGET_HEIGHT_BREAKPOINTS.relaxed}px) {
    [data-widget-greeting-title="true"] {
      font-size: 20px !important;
      line-height: 26px !important;
    }
    [data-widget-greeting-subtitle="true"] {
      font-size: 13px !important;
      line-height: 18px !important;
    }
    [data-widget-starter="true"] {
      font-size: 15px !important;
      padding: 10px 14px !important;
      border-radius: 12px !important;
      gap: 10px !important;
    }
    [data-widget-starter="true"] svg {
      width: 19px !important;
      height: 19px !important;
    }
  }

  @media (max-height: ${WIDGET_HEIGHT_BREAKPOINTS.compact}px) {
    [data-widget-greeting-title="true"] {
      font-size: 18px !important;
      line-height: 24px !important;
    }
    [data-widget-greeting-subtitle="true"] {
      font-size: 14px !important;
      line-height: 17px !important;
    }
    [data-widget-starter="true"] {
      font-size: 14px !important;
      padding: 9px 13px !important;
      gap: 9px !important;
    }
    [data-widget-starter="true"] svg {
      width: 18px !important;
      height: 18px !important;
    }
  }

  @media (max-height: ${WIDGET_HEIGHT_BREAKPOINTS.tight}px) {
    [data-widget-greeting-subtitle="true"] {
      display: none !important;
    }
    [data-widget-starter="true"] {
      font-size: 13px !important;
      padding: 8px 12px !important;
      border-radius: 12px !important;
      gap: 8px !important;
    }
    [data-widget-starter="true"] svg {
      width: 17px !important;
      height: 17px !important;
    }
  }
`

interface BodyScrollLockSnapshot {
  left: string
  overflowY: string
  position: string
  right: string
  scrollX: number
  scrollY: number
  top: string
}

let bodyScrollLockCount = 0
let bodyScrollLockSnapshot: BodyScrollLockSnapshot | null = null

const lockBodyScroll = (): void => {
  const body = document.body

  if (bodyScrollLockCount === 0) {
    bodyScrollLockSnapshot = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      overflowY: body.style.overflowY,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
    }

    body.style.position = "fixed"
    body.style.top = `-${window.scrollY}px`
    body.style.left = "0"
    body.style.right = "0"
    body.style.overflowY = "scroll"
  }

  bodyScrollLockCount += 1
}

const unlockBodyScroll = (): void => {
  if (bodyScrollLockCount === 0) return

  bodyScrollLockCount -= 1
  if (bodyScrollLockCount > 0) return

  const snapshot = bodyScrollLockSnapshot
  bodyScrollLockSnapshot = null
  if (!snapshot) return

  const body = document.body
  body.style.position = snapshot.position
  body.style.top = snapshot.top
  body.style.left = snapshot.left
  body.style.right = snapshot.right
  body.style.overflowY = snapshot.overflowY
  window.scrollTo(snapshot.scrollX, snapshot.scrollY)
}

interface Props {
  displayMode?: "overlay" | "slideout"
  isOpen: boolean
  caseId: string | null
  starterPayload?: Ri2StarterBootstrapPayload | null
  starterMessage?: string | null
  onStarterMessageConsumed?: () => void
  onCaseCreated: (caseId: string) => void
  onNewConversation: () => void
  onMinimize: () => void
  onClose: () => void
  onUnreadMessage: (messageId: string) => void
  onLatestAssistantMessageChange: (messageId: string | null) => void
}

const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < WIDGET_MOBILE_BREAKPOINT,
  )

  useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth < WIDGET_MOBILE_BREAKPOINT)
    }

    window.addEventListener("resize", handleResize)
    return (): void => window.removeEventListener("resize", handleResize)
  }, [])

  return isMobile
}

export const ChatPanel: FC<Props> = ({
  displayMode = "overlay",
  isOpen,
  caseId,
  starterPayload,
  starterMessage,
  onStarterMessageConsumed,
  onCaseCreated,
  onNewConversation,
  onMinimize,
  onClose,
  onUnreadMessage,
  onLatestAssistantMessageChange,
}) => {
  const isMobile = useIsMobile()
  const isSlideout = displayMode === "slideout" && !isMobile

  // Lock body scroll on mobile so the page behind the panel can't be scrolled.
  // iOS ignores overflow:hidden on <body>, so we use position:fixed instead.
  // We snapshot every property we touch and restore them exactly on cleanup
  // so we never clobber styles the host page already had on <body>.
  useEffect(() => {
    if (!isMobile || !isOpen) return

    lockBodyScroll()

    return (): void => {
      unlockBodyScroll()
    }
  }, [isMobile, isOpen])

  const panelStyle: React.CSSProperties = isMobile
    ? {
        // Use top+bottom instead of height so the panel always fills the real
        // visible viewport on iOS (height:Xvh can be cut off by browser chrome).
        position: "fixed",
        top: 20,
        bottom: 0,
        right: 0,
        left: 0,
        borderTopLeftRadius: WIDGET_BORDER_RADIUS,
        borderTopRightRadius: WIDGET_BORDER_RADIUS,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        zIndex: 9999,
      }
    : isSlideout
      ? {
          // The #ri2-slideout-host flex child in CSS is position:sticky with
          // explicit height. The panel just fills that container.
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: 0,
          backgroundImage: SLIDEOUT_PANEL_BG,
          backgroundColor: "#0182ba",
        }
      : {
          position: "fixed",
          bottom: WIDGET_MARGIN,
          right: WIDGET_MARGIN,
          width: WIDGET_PANEL_WIDTH,
          height: WIDGET_PANEL_HEIGHT,
          maxHeight: "92vh",
          borderRadius: WIDGET_BORDER_RADIUS,
          zIndex: 9999,
        }

  const motionProps = isSlideout
    ? {
        initial: {opacity: 0, x: WIDGET_SLIDEOUT_WIDTH},
        animate: {opacity: 1, x: 0},
        exit: {opacity: 0, x: WIDGET_SLIDEOUT_WIDTH},
        transition: {
          type: "tween" as const,
          duration: 0.3,
          ease: "easeOut" as const,
        },
      }
    : {
        initial: {opacity: 0, y: 20, scale: 0.95},
        animate: {opacity: 1, y: 0, scale: 1},
        exit: {opacity: 0, y: 20, scale: 0.95},
        transition: {type: "spring" as const, stiffness: 300, damping: 25},
      }

  return (
    <motion.div
      {...motionProps}
      className={isSlideout && !caseId ? SLIDEOUT_ATTENTION_CLASS : undefined}
      style={{
        ...panelStyle,
        display: isOpen ? "flex" : "none",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow:
          "0 8px 40px rgba(0, 0, 0, 0.16), 0 2px 12px rgba(0, 0, 0, 0.1)",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      {isSlideout && <style>{SLIDEOUT_ATTENTION_CSS}</style>}
      <ChatPanelHeader
        displayMode={displayMode}
        onMinimize={onMinimize}
        onClose={onClose}
        onNewConversation={onNewConversation}
        caseId={caseId}
      />
      <shadowRoot.div
        id={SHADOW_HOST_ID}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
        }}
      >
        <style>{stylesheet}</style>
        <style>{WIDGET_COMPACT_CSS}</style>
        {isSlideout && <style>{SLIDEOUT_TYPOGRAPHY_CSS}</style>}
        {isSlideout && <style>{SLIDEOUT_BODY_WASH_CSS}</style>}
        <div
          id={APP_ROOT_ID}
          className={
            isSlideout
              ? caseId
                ? "ri2-slideout-body"
                : "ri2-slideout-body ri2-slideout-body-idle"
              : undefined
          }
          data-panda-theme="green"
          data-mode="dark"
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflow: "hidden",
            backgroundColor: isSlideout ? undefined : "#FAFAFA",
            fontSize: 16,
            lineHeight: "22px",
            fontFamily:
              'ABC Monument Grotesk, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          <MemoryRouter>
            <ChatPanelBody
              isOpen={isOpen}
              caseId={caseId}
              onCaseCreated={onCaseCreated}
              starterPayload={starterPayload}
              starterMessage={starterMessage}
              onStarterMessageConsumed={onStarterMessageConsumed}
              onUnreadMessage={onUnreadMessage}
              onLatestAssistantMessageChange={onLatestAssistantMessageChange}
            />
          </MemoryRouter>
        </div>
      </shadowRoot.div>
    </motion.div>
  )
}
